"use client";
import Icon from "@/components/icons/Icons";
import CustomCalendar from "@/components/ui/CustomCalendar";
import CustomDropdown from "@/components/ui/CustomDropdown";
import InformationButton from "@/components/ui/InformationButton";
import { useIsMobile } from "@/utils/useIsMobile";
import { format } from "date-fns";
import React, { useRef, useState } from "react";

interface CalcDateInputProps {
  label: string; // Label shown on the left
  keyword?: string; // Optional keyword icon
  defaultDate?: Date; // Optional default selected date
  minSelectableDate?: Date; // Optional min selectable date
  maxSelectableDate?: Date; // Optional max selectable date
  onDateChange?: (date: Date) => void; // Callback to send date to parent
  fontSizeBase?: boolean; // Optional font size control
}

export default function CalcDateInput({ label, keyword, defaultDate = new Date(), minSelectableDate = new Date(), maxSelectableDate = new Date(new Date().setFullYear(new Date().getFullYear() + 10)), onDateChange, fontSizeBase = false }: CalcDateInputProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(defaultDate);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setIsCalendarOpen(false);
    if (onDateChange) onDateChange(date);
  };

  const isMob = useIsMobile(631);

  return (
    <div className="flex justify-between items-center gap-0 xxxs:gap-2 mb-2 relative">
      {/* Label & Keyword Tooltip */}
      <div className="flex items-center gap-1">
        <span className={`font-semibold text-left ${fontSizeBase ? "text-sm sm:text-base lg:text-lg" : "text-2xs xxxs:text-sm sm:text-base lg:text-lg"} text-[#222222]`}>{label}</span>
        {keyword && <InformationButton keywords={keyword} className="w-4! h-4! sm:w-5! sm:h-5!" />}
      </div>

      {/* Date Picker Input */}
      <div className="max-w-24 xxxs:max-w-26.5 xs:max-w-40 w-full group">
        <button ref={buttonRef} onClick={() => setIsCalendarOpen((prev) => !prev)} className={`w-full py-2 px-3 rounded-lg text-left bg-white border font-medium text-xs sm:text-sm lg:text-base text-primaryGreen transition-all duration-200 flex items-center justify-start xs:justify-between gap-2 ${isCalendarOpen ? "border-[#a0a4ac]" : "border-[#cad3e2] hover:border-[#a0a4ac]"}`}>
          {selectedDate ? format(selectedDate, "dd-MM-yyyy") : "Select date"}
          {!isMob && <Icon.calendarIcon className={`hidden xs:block transition duration-350 ${isCalendarOpen ? "text-primaryGreen" : "text-[#006b66a4] group-hover:text-primaryGreen"}`} />}
        </button>

        {/* Dropdown Calendar */}
        <CustomDropdown isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} className="top-full right-0 w-full xs:w-2/3 mt-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 p-1.5 overflow-hidden" excludeRefs={[buttonRef]}>
          <CustomCalendar defaultSelectedDate={selectedDate} onDateSelect={handleDateSelect} minSelectableDate={minSelectableDate} maxSelectableDate={maxSelectableDate} fontSizeBase={fontSizeBase} />
        </CustomDropdown>
      </div>
    </div>
  );
}
